import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { features, steps, testimonials, faqs } from '@/mocks/features';
import { categories } from '@/mocks/categories';
import { getFeaturedArticles } from '@/mocks/articles';

export default function HomePage() {
  const [activeFaq, setActiveFaq] = useState<number | null>(null);
  const [scrolled, setScrolled] = useState(false);
  const featuredArticles = getFeaturedArticles();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 300);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '17起草视频',
    url: 'https://17draft.com',
    description: '专业视频起草制作平台，帮助创作者快速完成视频脚本策划、素材整理与初稿输出',
    email: 'contact@17draft.com',
    telephone: '400-888-8888',
    address: {
      '@type': 'PostalAddress',
      addressLocality: '北京',
      addressRegion: '北京',
      addressCountry: 'CN',
    },
  };

  const webSiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '17起草视频',
    url: 'https://17draft.com',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://17draft.com/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(webSiteSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <header className="relative w-full h-[420px] md:h-[600px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Warm%20artistic%20abstract%20composition%20with%20flowing%20geometric%20shapes%20in%20coral%20orange%20and%20cream%20tones%2C%20soft%20gradient%20background%20with%20subtle%20texture%2C%20modern%20minimalist%20style%2C%20creative%20and%20inspiring%20atmosphere%2C%20digital%20art%20with%20film%20and%20video%20elements%20subtly%20blended%20into%20abstract%20forms&width=1800&height=900&seq=hero-17draft-01&orientation=landscape"
            alt="17起草视频 - 专业视频起草制作平台"
            title="17起草视频 - 专业视频起草制作平台"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/20 to-black/45"></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-full max-w-3xl mx-auto px-4 text-center">
              <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-background-50 mb-4 md:mb-6 leading-tight">
                专业视频起草制作平台
              </h1>
              <p className="text-sm md:text-lg text-background-50/85 mb-6 md:mb-8 max-w-2xl mx-auto leading-relaxed">
                从脚本策划到素材整理，从初稿输出到成片管理，17起草视频让每一位创作者都能轻松实现从灵感到成片的全流程高效创作。
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <Link
                  to="/about"
                  className="px-8 py-3 rounded-lg bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  免费开始使用
                </Link>
                <Link
                  to="/category/video-script"
                  className="px-8 py-3 rounded-lg bg-background-50/15 backdrop-blur-sm text-background-50 font-medium text-sm hover:bg-background-50/25 transition-colors whitespace-nowrap cursor-pointer border border-background-50/30"
                >
                  浏览内容
                </Link>
              </div>
            </div>
          </div>
        </header>

        <main>
          <section className="py-14 md:py-20 bg-background-50">
            <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
              <div className="text-center mb-10 md:mb-14">
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                  核心功能
                </h2>
                <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto">
                  六大核心功能，覆盖视频创作全流程，让创作变得简单高效
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {features.map((feat) => (
                  <div
                    key={feat.id}
                    className="p-5 md:p-6 rounded-xl bg-background-100 border border-background-200/70 hover:border-primary-200 transition-all cursor-pointer group"
                  >
                    <div className="w-11 h-11 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 mb-4 group-hover:bg-primary-500 group-hover:text-background-50 transition-colors">
                      <i className={`${feat.icon} text-xl`}></i>
                    </div>
                    <h3 className="text-base md:text-lg font-heading font-semibold text-foreground-900 mb-2">
                      {feat.title}
                    </h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      {feat.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="py-14 md:py-20 bg-background-100">
            <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
              <div className="text-center mb-10 md:mb-14">
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                  使用流程
                </h2>
                <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto">
                  四步完成视频起草，简单直观的操作流程
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
                {steps.map((s) => (
                  <div key={s.step} className="text-center">
                    <div className="w-14 h-14 md:w-16 md:h-16 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-accent-100 text-accent-600">
                      <i className={`${s.icon} text-2xl md:text-3xl`}></i>
                    </div>
                    <div className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-accent-500 text-background-50 text-xs font-bold mb-3">
                      {s.step}
                    </div>
                    <h3 className="text-base font-heading font-semibold text-foreground-900 mb-2">
                      {s.title}
                    </h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      {s.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="py-14 md:py-20 bg-background-50">
            <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
              <div className="flex flex-col md:flex-row items-end justify-between mb-10 md:mb-14">
                <div>
                  <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                    内容分类
                  </h2>
                  <p className="text-sm md:text-base text-foreground-600">
                    按需浏览不同视频创作领域的内容
                  </p>
                </div>
                <Link
                  to="/category/video-script"
                  className="mt-4 md:mt-0 text-sm text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1 cursor-pointer"
                >
                  查看全部 <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className="group p-5 md:p-6 rounded-xl bg-background-100 border border-background-200/70 hover:border-primary-300 transition-all cursor-pointer"
                  >
                    <div className="w-11 h-11 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600 mb-4 group-hover:bg-primary-500 group-hover:text-background-50 transition-colors">
                      <i className={`${cat.icon} text-xl`}></i>
                    </div>
                    <h3 className="text-base font-heading font-semibold text-foreground-900 mb-1.5 group-hover:text-primary-600 transition-colors">
                      {cat.name}
                    </h3>
                    <p className="text-sm text-foreground-600 leading-relaxed mb-3">
                      {cat.description}
                    </p>
                    <span className="text-xs text-foreground-500">
                      {cat.articleCount} 篇文章
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          </section>

          <section className="py-14 md:py-20 bg-background-100">
            <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
              <div className="flex flex-col md:flex-row items-end justify-between mb-10 md:mb-14">
                <div>
                  <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                    精选文章
                  </h2>
                  <p className="text-sm md:text-base text-foreground-600">
                    专业创作知识与实战经验分享
                  </p>
                </div>
                <Link
                  to="/category/video-script"
                  className="mt-4 md:mt-0 text-sm text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1 cursor-pointer"
                >
                  更多文章 <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
                {featuredArticles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.id}`}
                    className="group rounded-xl overflow-hidden bg-background-50 border border-background-200/70 hover:border-primary-300 transition-all cursor-pointer"
                  >
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={article.image}
                        alt={article.title}
                        title={`${article.title} - 17起草视频`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute top-3 left-3">
                        <span className="px-2.5 py-1 rounded-md bg-primary-500 text-background-50 text-xs font-medium">
                          {article.categoryName}
                        </span>
                      </div>
                    </div>
                    <div className="p-4 md:p-5">
                      <h3 className="text-sm md:text-base font-heading font-semibold text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">
                        {article.title}
                      </h3>
                      <p className="text-xs md:text-sm text-foreground-600 mb-3 line-clamp-2 leading-relaxed">
                        {article.excerpt}
                      </p>
                      <div className="flex items-center justify-between text-xs text-foreground-500">
                        <span>{article.author}</span>
                        <span>{article.date} · {article.readTime}分钟阅读</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>

          <section className="py-14 md:py-20 bg-background-50">
            <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
              <div className="text-center mb-10 md:mb-14">
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                  用户口碑
                </h2>
                <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto">
                  来自真实用户的评价与反馈
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
                {testimonials.map((t) => (
                  <div
                    key={t.id}
                    className="p-5 md:p-6 rounded-xl bg-background-100 border border-background-200/70"
                  >
                    <div className="flex items-center gap-1 mb-3">
                      {Array.from({ length: t.rating }).map((_, i) => (
                        <i key={i} className="ri-star-fill text-accent-500 text-sm"></i>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                      &ldquo;{t.content}&rdquo;
                    </p>
                    <div className="flex items-center gap-3">
                      <img
                        src={t.avatar}
                        alt={t.name}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                      <div>
                        <p className="text-sm font-medium text-foreground-900">{t.name}</p>
                        <p className="text-xs text-foreground-500">{t.role}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="py-14 md:py-20 bg-background-100">
            <div className="w-full max-w-3xl mx-auto px-4 md:px-6">
              <div className="text-center mb-10 md:mb-14">
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                  常见问题
                </h2>
                <p className="text-sm md:text-base text-foreground-600">
                  您可能关心的问题，这里都有答案
                </p>
              </div>
              <div className="space-y-3">
                {faqs.map((faq, index) => (
                  <div
                    key={index}
                    className="rounded-xl bg-background-50 border border-background-200/70 overflow-hidden"
                  >
                    <button
                      className="w-full flex items-center justify-between p-4 md:p-5 text-left cursor-pointer"
                      onClick={() => setActiveFaq(activeFaq === index ? null : index)}
                    >
                      <h3 className="text-sm md:text-base font-medium text-foreground-900 pr-4">
                        {faq.question}
                      </h3>
                      <div className={`w-6 h-6 flex items-center justify-center shrink-0 rounded-full transition-colors ${
                        activeFaq === index ? 'bg-primary-500 text-background-50' : 'bg-background-200 text-foreground-500'
                      }`}>
                        <i className={`ri-${activeFaq === index ? 'subtract' : 'add'}-line text-sm`}></i>
                      </div>
                    </button>
                    {activeFaq === index && (
                      <div className="px-4 md:px-5 pb-4 md:pb-5">
                        <p className="text-sm text-foreground-600 leading-relaxed">
                          {faq.answer}
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="py-14 md:py-20 relative overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Abstract%20warm%20gradient%20background%20with%20flowing%20organic%20shapes%2C%20coral%20and%20cream%20tones%2C%20soft%20artistic%20texture%2C%20modern%20minimal%20design%2C%20inspiring%20creative%20atmosphere&width=1800&height=600&seq=cta-bg-17draft&orientation=landscape"
              alt=""
              className="absolute inset-0 w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-primary-600/90 to-accent-600/80"></div>
            <div className="relative z-10 w-full max-w-3xl mx-auto px-4 md:px-6 text-center py-10">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-background-50 mb-4">
                准备好开始高效创作了吗？
              </h2>
              <p className="text-sm md:text-base text-background-50/85 mb-6 md:mb-8 max-w-xl mx-auto">
                免费注册17起草视频，体验AI驱动的视频起草制作工具，让创作效率提升300%
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <Link
                  to="/about"
                  className="px-8 py-3 rounded-lg bg-background-50 text-primary-600 font-medium text-sm hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
                >
                  免费开始使用
                </Link>
                <Link
                  to="/category/video-script"
                  className="px-8 py-3 rounded-lg bg-transparent text-background-50 font-medium text-sm hover:bg-background-50/15 transition-colors whitespace-nowrap cursor-pointer border border-background-50/40"
                >
                  了解更多
                </Link>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}